/**
 * canonicalFields — N-14 conformant field accessors.
 *
 * RULE (N-14, D-235, 2026-08-03): every aliased field has ONE canonical key,
 * ONE accessor, and disagreement is LOGGED — never silently preferred.
 *
 * CONVENTION: canonical key = the spelling the WRITER emits.
 *   - Go-written fields are snake_case (Go struct JSON tags: `json:"customer_id"`).
 *   - Frontend-only fields are camelCase (assembleQuotePayload spreads them).
 *   - Never guess — import the accessor.
 *
 * Each accessor:
 *   1. Names the canonical key (what the writer emits)
 *   2. Lists accepted read aliases (why each exists)
 *   3. On disagreement: logs a warning and returns canonical (not silent prefer-first)
 *
 * Consumers import these instead of writing inline || / ?? chains.
 *
 * @module utils/canonicalFields
 */

/* eslint-disable no-console */

/**
 * Log a divergence warning. In production this would go to telemetry;
 * for now it's console.warn so it surfaces in dev tools.
 */
function warnDivergence(field, recordId, canonValue, aliasKey, aliasValue) {
  console.warn(
    `[N-14 DIVERGENCE] field="${field}" record="${recordId || '?'}": ` +
    `canonical=${JSON.stringify(canonValue)}, alias "${aliasKey}"=${JSON.stringify(aliasValue)}. ` +
    `Returning canonical. This record has conflicting data.`
  );
}

/**
 * Generic accessor factory. Returns a function that reads a field from a record
 * using the canonical key first, then aliases, with divergence detection.
 *
 * @param {string} canonicalKey - The canonical field name (what the writer emits)
 * @param {string[]} aliases - Read aliases in priority order
 * @param {object} [opts]
 * @param {*} [opts.defaultValue=null] - Value when all keys are absent
 */
function makeAccessor(canonicalKey, aliases, opts = {}) {
  const { defaultValue = null } = opts;

  return function accessor(record, recordId) {
    if (!record) return defaultValue;

    // Find the first non-null value
    let canonValue = undefined;
    let canonFound = false;

    // Check canonical key first
    if (record[canonicalKey] != null && record[canonicalKey] !== '') {
      canonValue = record[canonicalKey];
      canonFound = true;
    }

    // Check aliases — if canonical was found, check for disagreement
    for (const alias of aliases) {
      const aliasVal = record[alias];
      if (aliasVal != null && aliasVal !== '') {
        if (canonFound) {
          // Disagreement check: both exist with different values
          if (String(canonValue) !== String(aliasVal)) {
            warnDivergence(canonicalKey, recordId || record.id, canonValue, alias, aliasVal);
          }
        } else {
          // Canonical absent, use alias
          canonValue = aliasVal;
          canonFound = true;
        }
      }
    }

    return canonFound ? canonValue : defaultValue;
  };
}

// ─── T&C Template ────────────────────────────────────────────────────────────
// Canonical: `tcTemplateId` (top-level, camelCase)
// Writer: assembleQuotePayload (quotePrefill.js) via ...cleanRestMetadata spread.
//   Go has NO typed field for this — it rides the untyped map[string]interface{}.
//   "Canonical writer" = whatever the frontend last sent. Not a typed contract.
// Aliases:
//   - `tc_template_id`: backend snake_case normalisation (Go JSON tags)
//   - `metadata.tcTemplateId`: legacy nested path (never written, historical)
//   - `metadata.tc_template_id`: legacy nested path (never written, historical)
export const getTcTemplateId = (record, recordId) => {
  if (!record) return null;
  // Flatten: check top-level and metadata sub-object
  const candidates = {
    tcTemplateId: record.tcTemplateId,
    tc_template_id: record.tc_template_id,
    'metadata.tcTemplateId': record.metadata?.tcTemplateId,
    'metadata.tc_template_id': record.metadata?.tc_template_id,
  };

  let canon = null;

  for (const [key, val] of Object.entries(candidates)) {
    if (val != null && val !== '') {
      if (canon === null) {
        canon = val;
      } else if (String(canon) !== String(val)) {
        warnDivergence('tcTemplateId', recordId || record.id, canon, key, val);
      }
    }
  }

  return canon;
};

// ─── Money fields ────────────────────────────────────────────────────────────
// Canonical: camelCase (what the frontend writes via assembleQuotePayload)
// Aliases: snake_case (what the Go backend returns via JSON tags)

// totalAmount — the quote/job/invoice total in cents or dollars
// Writer: assembleQuotePayload (total_amount key), Go backend (total_amount JSON tag)
// Note: canonical is ambiguous (both writers exist). Prefer total_amount (backend is source of truth).
export const getTotalAmount = makeAccessor('total_amount', ['totalAmount', 'total'], { defaultValue: 0 });

// lineTotal — per-line-item total.
// Writer (current): serializeLineItem emits `line_total`; frontend also writes `lineTotal` (dual-write).
// Writer (legacy):  older line items on migrated quotes carry `total` (Q-0029 fixture, 2026-08-07).
//   Adding `total` as an alias is a SAFETY REQUIREMENT before any renderer migration — without it,
//   base lines on migrated quotes would silently return the defaultValue.
export const getLineTotal = makeAccessor('line_total', ['lineTotal', 'total'], { defaultValue: 0 });

// unitRate — per-line-item unit rate
// Writer: serializeLineItem (unit_rate key)
export const getUnitRate = makeAccessor('unit_rate', ['unitRate', 'unit_price', 'unitPrice'], { defaultValue: 0 });

// costBasis — per-line-item cost basis (pre-margin).
// Writer (current): serializeLineItem emits `cost_basis`; frontend also writes `costBasis`.
// Writer (legacy):  older line items on migrated quotes carry `cost` (Q-0029 fixture).
//   Same safety rationale as getLineTotal above.
export const getCostBasis = makeAccessor('cost_basis', ['costBasis', 'cost'], { defaultValue: 0 });

// ─── Identity fields ─────────────────────────────────────────────────────────


// customerId - the foreign key linking a quote/job/invoice to a customer.
// CRITICAL (N-14 D-235): 40 quote records carry TWO DIFFERENT customer UUIDs.
//    This is the most dangerous divergence in the dataset - wrong OWNER, not wrong number.
//
// Writer: Go typed structs emit `customer_id` (json:"customer_id" dynamodbav:"customer_id").
// Bug: the quotes Lambda handler (untyped map) writes `customerId` (camelCase) to DynamoDB.
//      Both keys coexist on the same record with DIFFERENT values.
// Canon: `customer_id` (the typed struct contract; the handler is the bug).
export const getCustomerId = makeAccessor('customer_id', ['customerId'], { defaultValue: null });
// customerName
// Writer: Go backend (customer_name JSON tag)
export const getCustomerName = makeAccessor('customer_name', ['customerName'], { defaultValue: '' });

// quoteNumber — the human-facing quote identifier.
//
// Writer (verified 2026-08-17 at bac2dd6): cmd/quotes/main.go:503 emits
// `body["quoteNumber"] = generateQuoteNumber()`. No JSON tag exists — the
// quote payload is a `map[string]interface{}`, not a typed struct. The
// migration does NOT write a quote number for quotes (it writes
// `projectNumber` for jobs; see getJobNumber below).
//
// Canon: `quoteNumber` (camelCase, the spelling the writer emits).
// Alias: `quote_number` (snake_case, legacy read-fallback preserved for
//        any pre-existing records that carry it).
//
// Rationale (328_ §3): the file's own convention is "canonical = the spelling
// the WRITER emits." The previous canon was `quote_number` — an inversion.
// This fix landed the file in obedience of its own docblock; no user-facing
// behaviour changes today because ~20 display sites read raw
// `xxxNumber || xxx_number` chains directly and never call this accessor.
// See PR-38 body for the adoption story.
export const getQuoteNumber = makeAccessor('quoteNumber', ['quote_number'], { defaultValue: null });

// lineItems — the array of line items on a quote/job/invoice
// Writer: Go backend (line_items JSON tag), frontend (lineItems in some paths)
export const getLineItems = makeAccessor('line_items', ['lineItems'], { defaultValue: [] });

// workOrders — nested WO array on a job
// Writer: Go backend (work_orders key in DynamoDB map)
export const getWorkOrders = makeAccessor('work_orders', ['workOrders'], { defaultValue: [] });

// ─── Display identifiers (numbers a user reads) ──────────────────────────────
// These have a POPULATION SPLIT rather than a casing alias.
// Migrated records have one field, net-new records have another. Both are
// legitimate writers; there is no "one canon" to prefer. The accessor resolves
// to whichever exists. Divergence (both present with different values) is
// still logged — it would indicate a data migration bug, not an alias split.
//
// This unifies the previously-separate displayId.js chains under one alias
// system. `displayId.js` now calls these accessors.

// getJobNumber — the population split, NOT a casing alias.
//
// Migrated writer: migration/cmd/migrate/entities.go:1068 (inside migrateJobs)
//   emits `projectNumber` via putIf(item, "projectNumber", trim(row["Quote_ProjectNumber"])).
//   Verified 2026-08-17 at bac2dd6. Line 168 does the same in migrateQuotes.
// Net-new writer: bidit/backend-go/cmd/jobs/main.go:298 emits `jobNumber`
//   at job creation. Verified 2026-08-17 at bac2dd6.
//
// 🛑 DO NOT invert this. 328_ documents the round where an audit-tool false
//    negative claimed `projectNumber` was orphan (no writer) — the tool's
//    producer scan didn't match the `putIf(item, "key", v)` idiom. Inverting
//    to `jobNumber` as canonical would make every migrated job unfindable
//    by number. This block exists so the next reader (or the next audit
//    tool) sees the proof at source and does not re-litigate.
//
// Both keys are stored top-level; no casing variants (yet).
export const getJobNumber = makeAccessor(
  'projectNumber',
  ['project_number', 'jobNumber', 'job_number'],
  { defaultValue: null }
);

// getWorkOrderNumber
// Migrated writer: migration/entities.go emits `number` (e.g. "34696-1")
// Net-new writer:  jobs Lambda emits `number` at WO creation
// Aliases exist for legacy code paths that used camelCase.
// getWorkOrderNumber
// Writers: app-created WOs emit `number` (e.g. "38369-2").
// `woNumber` is the LEGACY INTEGER ID (e.g. "17427") — a different concept,
// not an alias. Removed from alias list to stop N-14 false positives.
export const getWorkOrderNumber = makeAccessor(
  'number',
  [],
  { defaultValue: null }
);

// getInvoiceNumber
export const getInvoiceNumber = makeAccessor(
  'invoiceNumber',
  ['invoice_number'],
  { defaultValue: null }
);

// getChangeOrderNumber
export const getChangeOrderNumber = makeAccessor(
  'coNumber',
  ['co_number'],
  { defaultValue: null }
);

// PR-16: canonical product/line-item name accessor.
// Writers: migration embed emits `name`; legacy rows carry `item_name`;
//          app-created lines carry `description`.
export const getLineItemName = makeAccessor(
  'name',
  ['item_name', 'description', 'productLabel'],
  { defaultValue: null }
);

// PR-72 Commit 5 (R-90): canonical work-order schedule-date accessor.
//
// Canonical `scheduledDate`, per cmd/jobs/main.go:504-507 whose own
// comment reads "frontend reads scheduledDate" — the writer's stated
// contract.
//
// Aliases in priority order:
//   scheduled_date  backend rescheduleWorkOrder writes both cases
//                   (cmd/jobs/main.go:820-821); a projection or partial
//                   read could deliver only the snake_case.
//   startDate       WOCreateSheets.js:184 writes this today (a range,
//                   not a single day — see the range caveat below).
//                   Commit 5's writer fix has the sheet emit
//                   scheduledDate alongside startDate; the accessor
//                   heals records already written before that.
//   start_date      snake_case migration variant.
//   startAt         legacy key that enrichWorkOrders maps from on
//                   read. Records reaching a caller before enrichment
//                   (e.g. tests, or a direct DDB fetch) resolve here.
//
// ── Range-vs-day: RESOLVED (dispatch 419 §2) ─────────────────────────
//
// `scheduledDate` is the START of the work order.
// `endDate` carries the SPAN.
// `startDate` is a redundant spelling of `scheduledDate`, not the
// other half of a pair.
//
// Every writer in the corpus keeps `scheduledDate` and `startDate`
// coincident:
//   - create sheet (Commit 5): scheduledDate = form.startDate;
//     startDate = form.startDate
//   - rescheduleWorkOrder (main.go:820-823): writes scheduledDate,
//     scheduled_date, startDate, start_date to the SAME value
//   - enricher (main.go:504-507): maps startAt → scheduledDate;
//     leaves startDate alone (it may not exist on legacy records)
//
// No writer produces a WO where `scheduledDate` and `startDate` differ.
// The "two distinct concepts" reading has no writer behind it.
//
// Consequence: a divergence warning from getScheduledDate means legacy
// or out-of-band data, NEVER normal use. Reschedules do not spam this
// warning because they update all four keys together.
export const getScheduledDate = makeAccessor(
  'scheduledDate',
  ['scheduled_date', 'startDate', 'start_date', 'startAt'],
  { defaultValue: null }
);

// ─── Scheduled start/stop times ──────────────────────────────────────────────
// IDI-471 (2026-10-14): the IDI-185 getScheduledStartTime / getScheduledEndTime
// accessors were replaced by utils/woSchedule, which reads the start AND the
// stop (with its own date: endDate / stopAt) in one place. They assumed WOs
// are single-day, which is the bug IDI-471 fixes.
